<template>
  <BaseCard class="max-h-180 overflow-x-auto">
    <LoadingWrapper
      v-if="meteringPoint"
      :error="loadingFailed"
      :loading="loading"
    >
      <BaseTable
        :columns="columns"
        :rows="rows"
        :precision="PRECISION"
        class="min-h-24"
        @row-clicked="onRowClicked"
      >
        <!-- Add header tooltips -->
        <template
          v-for="({ wikiPath, isResult }, key, index) in columns"
          :key="key"
          #[key]="{ header }"
        >
          <div>
            <span :class="{ 'text-gray-350': !isResult }">{{ header }}</span
            ><span class="whitespace-nowrap"
              >&nbsp;<WikiTooltipComponent
                v-if="wikiPath"
                :path="wikiPath"
                :position="
                  index === Object.keys(columns).length - 1
                    ? 'bottom left'
                    : 'bottom'
                "
              />
            </span>
          </div>
        </template>
        <!-- Bold total column -->
        <template #TOTAL-cell="{ row }">
          <div class="font-bold text-gray-500">
            {{ formatNumber(row.TOTAL, PRECISION) }}
          </div>
        </template>
        <template #TOTAL-summary="{ row }">
          <div class="font-bold text-gray-500">
            {{ formatNumber(row.TOTAL, PRECISION) }}
          </div>
        </template>
        <!-- Columns with extra formatting -->
        <template
          v-for="(column, key) in formattedColumns"
          #[`${key}-cell`]="{ row }"
        >
          {{ formatCellValue(row[key], column) }}
        </template>
        <template
          v-for="(column, key) in formattedColumns"
          #[`${key}-summary`]="{ row }"
        >
          {{ formatCellValue(row[key], column) }}
        </template>
        <!-- Italic summary cell -->
        <template #datetime-summary="{ row }">
          <span class="italic">
            {{ row.datetime }}
          </span>
        </template>
        <!-- Format dates and add badges to datetime cells -->
        <template #datetime-cell="{ row }">
          <div class="flex justify-between gap-1">
            <span>{{ format(row.datetime, dateFormat[bucketSize]) }}</span>
            <BaseBadge
              v-if="isToday(row.datetime) && bucketSize === BUCKET.DAY"
              color="primary"
            >
              Vandaag
            </BaseBadge>
            <BaseTooltip
              v-else-if="isIncompleteRow(row.datetime)"
              :description="`Datumselectie dekt niet ${bucketSize === BUCKET.MONTH ? 'de hele maand' : 'het hele jaar'}`"
            >
              <BaseBadge color="danger"> Onvolledig </BaseBadge>
            </BaseTooltip>
            <BaseTooltip
              v-else-if="!row.stable"
              description="Op basis van voorlopige gegevens"
            >
              <BaseBadge class="bg-energy-50! text-energy-900!">
                Voorlopig
              </BaseBadge>
            </BaseTooltip>
          </div>
        </template>
      </BaseTable>
    </LoadingWrapper>
    <div v-else class="flex items-center justify-center p-4 italic md:p-6">
      <p>Maak een selectie om gegevens te kunnen bekijken</p>
    </div>
  </BaseCard>
</template>

<script setup>
import { computed } from "vue"
import {
  format,
  isToday,
  differenceInDays,
  startOfYear,
  endOfYear,
  startOfMonth,
  endOfMonth,
  startOfDay,
  startOfToday,
} from "date-fns"
import { formatNumber, formatBoolean } from "@/helpers/formatter.js"
import { MODE, BUCKET } from "@/helpers/filter.js"
import {
  default as useResultsData,
  FORCE_PROVISIONAL,
} from "@/composables/useResultsData.js"
import {
  BaseTable,
  BaseBadge,
  BaseCard,
  BaseTooltip,
  LoadingWrapper,
} from "@repowerednl/ui-component-library"
import WikiTooltipComponent from "@/components/information/WikiTooltipComponent.vue"

const props = defineProps({
  /**
   * Can be either an asset or a portfolio, but no data is shown for portfolios.
   */
  meteringPoint: {
    type: Object,
    default: null,
  },
  /**
   * Array of two Date objects; the last day is included in the range.
   */
  dateRange: {
    type: Array,
    required: true,
  },
  /**
   * Determines which data series are shown/hidden.
   */
  mode: {
    required: true,
    validator: (value) => Object.values(MODE).includes(value),
  },
  /**
   * Whether the chart is shown in the Insights panel.
   */
  asInsights: Boolean,
})

const emit = defineEmits(["zoom"])

const PRECISION = 2
const PRECISION_EXTRA = 5
const NA_STRING = "\u2013" // En dash
const RESULT_UNIT = "€"

const { recordsData, loading, loadingFailed, bucketSize } = useResultsData(
  () => props.meteringPoint,
  () => props.dateRange,
)

const dateFormat = {
  [BUCKET.MINUTE]: "PPp",
  [BUCKET.DAY]: "PP",
  [BUCKET.MONTH]: "MMMM y",
  [BUCKET.YEAR]: "y",
}

/**
 * Test whether a date key implies the row is incomplete, i.e. today lies in the
 * row's date range.
 */
function isIncompleteRow(datetimeKey) {
  switch (bucketSize.value) {
    case BUCKET.MONTH:
      return (
        differenceInDays(props.dateRange[0], startOfMonth(datetimeKey)) > 0 ||
        differenceInDays(props.dateRange[1], endOfMonth(datetimeKey)) < 0
      )

    case BUCKET.YEAR:
      return (
        differenceInDays(props.dateRange[0], startOfYear(datetimeKey)) > 0 ||
        differenceInDays(props.dateRange[1], endOfYear(datetimeKey)) < 0
      )
    default:
      return false
  }
}

/**
 * Columns that are hidden in the different modes. Unitless values (empty
 * string) are always hidden.
 */
const modeHiddenColumns = {
  [MODE.FINANCE]: ["kWh", ""],
  [MODE.BOTH]: [""],
  // MODE.ENERGY: hide all except 'kWh'
}

function isHiddenUnit(unit) {
  if (props.mode === MODE.ENERGY) {
    return unit !== "kWh"
  } else {
    return !!modeHiddenColumns[props.mode]?.includes(unit)
  }
}

/**
 * Convert record type data to column data for the BaseTable component.
 */
function buildColumnData(spec, isBoolean) {
  const unitLabel =
    spec.unit && spec.unit !== "boolean" ? ` (${spec.unit})` : ""
  // Parent-child relationships are ignored and not shown for energy mode.
  const parent =
    props.mode !== MODE.ENERGY &&
    spec.parent_record_type in recordsData.value.types &&
    spec.parent_record_type

  return {
    parent,
    label: `${spec.label?.nl} ${unitLabel}`,
    group: spec.category?.nl,
    classes: [
      "text-right tabular-nums",
      spec.is_total_result ? "font-bold" : "",
    ].join(" "),
    // Custom values
    wikiPath: spec.wiki_path?.nl,
    isHighPrecision: spec.is_high_precision,
    zeroAsNa: spec.zero_as_na,
    isBoolean,
    isResult: spec.unit === RESULT_UNIT,
  }
}

const columns = computed(() => {
  if (!recordsData.value) {
    return {}
  }
  // Always show the datetime column
  const entries = [["datetime", { label: "Datum" }]]

  // Order types by their order property
  const sortedTypes = Object.entries(recordsData.value.types).sort((a, b) => {
    return a[1].order - b[1].order
  })
  const records = Object.values(recordsData.value.records)

  // Construct input for the BaseTable component
  for (const [type, spec] of sortedTypes) {
    const isHiddenByContext = props.asInsights
      ? spec.is_hidden_insights
      : spec.is_hidden_results
    let includeType =
      !isHiddenUnit(spec.unit) && !spec.is_hidden && !isHiddenByContext
    // Hide all-zero columns if the spec has is_hidden_if_all_zero
    if (includeType && spec.is_hidden_if_all_zero) {
      includeType = records.some((record) => record.data[type]?.value)
    }
    // Add the current column if it should be included
    if (includeType) {
      // Boolean record types (e.g. 'aFRR bid submitted') are rendered with yes/no labels.
      const isBoolean = spec.unit === "boolean"
      entries.push([type, buildColumnData(spec, isBoolean)])
    }
  }
  return Object.fromEntries(entries)
})

/**
 * Flattens a record entry to pass to the BaseTable component's 'row' prop.
 * Before:
 *   [
 *     "2000-01-01T00:00:00.000Z",
 *     {
 *       "data": {
 *         "Type A": {
 *           "value": 0,
 *           "stable": true
 *         },
 *         "Type B": {
 *           "value": 0,
 *           "stable": false
 *         },
 *       },
 *     },
 *   ]
 * After:
 *   {
 *     _id: "cell",
 *     datetime: new Date("2000-01-01T00:00:00.000Z"),
 *     stable: false,
 *     "Type A": 0,
 *     "Type B": 0,
 *   }
 */
function flattenRecord(entry, clickable) {
  const [key, { data }] = entry
  const values = {}
  const isTotal = key === "Totaal"
  const datetime = isTotal ? key : new Date(key)
  // Set stable to false prematurely if provisional status should be forced
  let stable =
    isTotal ||
    differenceInDays(startOfToday(), startOfDay(datetime)) >= FORCE_PROVISIONAL

  for (const [recordType, content] of Object.entries(data)) {
    values[recordType] = content.value
    stable &&= content.stable
  }

  return {
    _id: key === "Totaal" ? "summary" : "cell",
    _clickable: clickable,
    datetime,
    stable,
    ...values,
  }
}

const rows = computed(() => {
  if (!recordsData.value) {
    return []
  }
  const clickableRows = bucketSize.value !== BUCKET.MINUTE
  const clickableSummary = [BUCKET.DAY, BUCKET.MINUTE].includes(
    bucketSize.value,
  )
  const { records, totals } = recordsData.value
  return [
    flattenRecord(["Totaal", totals], clickableSummary),
    ...Object.entries(records).map((entry) =>
      flattenRecord(entry, clickableRows),
    ),
  ]
})

/**
 * Collects all columns that require extra formatting:
 * - High precision columns
 * - Columns that should show an n/a-like string for zero values
 * - Boolean columns shown with yes/no labels
 */
const formattedColumns = computed(() => {
  const filtered = Object.entries(columns.value).filter(
    ([_key, column]) =>
      column.isHighPrecision || column.zeroAsNa || column.isBoolean,
  )
  return Object.fromEntries(filtered)
})

/**
 * Formats a cell value based on the column's properties.
 */
function formatCellValue(value, column) {
  if (value === undefined) {
    return null
  } else if (column.isBoolean) {
    return formatBoolean(value)
  } else if (column.zeroAsNa && value === 0) {
    return NA_STRING
  } else if (column.isHighPrecision) {
    return formatNumber(value, PRECISION_EXTRA)
  } else {
    return formatNumber(value, PRECISION)
  }
}

function onRowClicked(row) {
  if (row._id === "summary") {
    // Drill up to the previous level
    switch (bucketSize.value) {
      case BUCKET.DAY:
        emit("zoom", [
          startOfYear(props.dateRange[0]),
          endOfYear(props.dateRange[0]),
        ])
        break
      case BUCKET.MINUTE:
        emit("zoom", [
          startOfMonth(props.dateRange[0]),
          endOfMonth(props.dateRange[0]),
        ])
        break
      default:
      // Do nothing
    }
  } else {
    // Drill down to the next level
    switch (bucketSize.value) {
      case BUCKET.YEAR:
        emit("zoom", [startOfYear(row.datetime), endOfYear(row.datetime)])
        break
      case BUCKET.MONTH:
        emit("zoom", [startOfMonth(row.datetime), endOfMonth(row.datetime)])
        break
      case BUCKET.DAY:
        emit("zoom", [startOfDay(row.datetime), startOfDay(row.datetime)])
        break
      default:
      // Do nothing
    }
  }
}
</script>
